const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

class ApiService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Study Builder API
  async getStudies() {
    return this.request<any[]>('/studies');
  }

  async getStudy(studyId: string) {
    return this.request<any>(`/studies/${studyId}`);
  }

  async createStudy(studyData: any) {
    return this.request<any>('/studies', {
      method: 'POST',
      body: JSON.stringify(studyData),
    });
  }

  async uploadMedia(studyId: string, formData: FormData) {
    return this.request<any>(`/studies/${studyId}/media`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  // Media Management API
  async getMediaFiles(studyId: string, filters?: {
    media_type?: string;
    content_category?: string;
    processing_status?: string;
  }) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]: [string, any]) => {
        if (value !== undefined) params.append(key, String(value));
      });
    }
    
    const queryString = params.toString();
    const endpoint = `/studies/${studyId}/media${queryString ? `?${queryString}` : ''}`;
    
    return this.request<any[]>(endpoint);
  }

  async uploadMediaFile(studyId: string, file: File, metadata: {
    media_type: string;
    content_category: string;
    ai_providers?: string[];
  }) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_type', metadata.media_type);
    formData.append('content_category', metadata.content_category);
    formData.append('ai_providers', JSON.stringify(metadata.ai_providers || ['openai']));

    return this.request<any>(`/studies/${studyId}/media/upload`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async updateMediaFile(studyId: string, fileId: string, updates: {
    media_type?: string;
    content_category?: string;
    ai_providers?: string[];
  }) {
    return this.request<any>(`/studies/${studyId}/media/${fileId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteMediaFile(studyId: string, fileId: string) {
    return this.request<any>(`/studies/${studyId}/media/${fileId}`, {
      method: 'DELETE',
    });
  }

  async getMediaProcessingJobs(studyId: string, fileId: string) {
    return this.request<any[]>(`/studies/${studyId}/media/${fileId}/processing-jobs`);
  }

  async reprocessMediaFile(studyId: string, fileId: string, aiProviders: string[]) {
    return this.request<any>(`/studies/${studyId}/media/${fileId}/reprocess`, {
      method: 'POST',
      body: JSON.stringify(aiProviders),
    });
  }

  async getMediaFilePreview(studyId: string, fileId: string) {
    return this.request<any>(`/studies/${studyId}/media/${fileId}/preview`);
  }

  async triggerAnalysis(studyId: string, templates: string[]) {
    return this.request<any>(`/studies/${studyId}/analyze`, {
      method: 'POST',
      body: JSON.stringify({ templates }),
    });
  }

  // Metadata Explorer API
  async getAnnotations(studyId: string, filters?: any) {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]: [string, any]) => {
        if (value !== undefined) params.append(key, String(value));
      });
    }
    
    const queryString = params.toString();
    const endpoint = `/studies/${studyId}/annotations${queryString ? `?${queryString}` : ''}`;
    
    return this.request<any[]>(endpoint);
  }

  async updateAnnotation(studyId: string, annotationId: string, updates: any) {
    return this.request<any>(`/studies/${studyId}/annotations/${annotationId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Insight Console API
  async getChatSessions(studyId: string) {
    return this.request<any[]>(`/studies/${studyId}/chat/sessions`);
  }

  async sendChatMessage(studyId: string, sessionId: string, message: string, useRag: boolean = true) {
    return this.request<any>(`/studies/${studyId}/chat/sessions/${sessionId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message, use_rag: useRag }),
    });
  }

  async callAnalysisFunction(studyId: string, functionName: string, parameters: any) {
    return this.request<any>(`/studies/${studyId}/analysis/functions/${functionName}`, {
      method: 'POST',
      body: JSON.stringify(parameters),
    });
  }

  // Insight Generator API
  async generateReport(studyId: string, reportConfig: any) {
    return this.request<any>(`/studies/${studyId}/reports/generate`, {
      method: 'POST',
      body: JSON.stringify(reportConfig),
    });
  }

  async getReports(studyId: string) {
    return this.request<any[]>(`/studies/${studyId}/reports`);
  }

  async downloadReport(reportId: string, format: string) {
    const response = await fetch(`${API_BASE_URL}/reports/${reportId}/download?format=${format}`);
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    return response.blob();
  }

  // Research Questions API
  async getResearchQuestions(studyId: string) {
    return this.request<any[]>(`/studies/${studyId}/research-questions`);
  }

  async createResearchQuestion(studyId: string, questionData: any) {
    return this.request<any>(`/studies/${studyId}/research-questions`, {
      method: 'POST',
      body: JSON.stringify(questionData),
    });
  }

  async updateResearchQuestion(studyId: string, questionId: string, questionData: any) {
    return this.request<any>(`/studies/${studyId}/research-questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify(questionData),
    });
  }

  async deleteResearchQuestion(studyId: string, questionId: string) {
    return this.request<any>(`/studies/${studyId}/research-questions/${questionId}`, {
      method: 'DELETE',
    });
  }

  async reorderResearchQuestions(studyId: string, questions: { question_id: string; new_order: number }[]) {
    return this.request<any>(`/studies/${studyId}/research-questions/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ questions }),
    });
  }

  // Configuration API
  async getMediaTypes(includeHidden: boolean = false) {
    const params = includeHidden ? '?include_hidden=true' : '';
    return this.request<any[]>(`/config/media-types${params}`);
  }

  async getContentCategories(includeHidden: boolean = false) {
    const params = includeHidden ? '?include_hidden=true' : '';
    return this.request<any[]>(`/config/content-categories${params}`);
  }

  async updateMediaType(mediaTypeId: string, updates: {
    label?: string;
    description?: string;
    file_extensions?: string[];
    display_order?: string;
    is_visible?: boolean;
  }) {
    return this.request<any>(`/admin/media-types/${mediaTypeId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async updateContentCategory(categoryId: string, updates: {
    label?: string;
    description?: string;
    display_order?: string;
    is_visible?: boolean;
  }) {
    return this.request<any>(`/admin/content-categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Health check
  async healthCheck() {
    return this.request<any>('/health');
  }

  // Service status
  async getServiceStatus() {
    return this.request<any>('/status');
  }

  // API Key Management
  async getAPIKeys(provider?: string, activeOnly: boolean = true) {
    try {
      const params = new URLSearchParams();
      if (provider) params.append('provider', provider);
      if (activeOnly) params.append('active_only', activeOnly.toString());
      
      const response = await fetch(`${API_BASE_URL}/admin/api-keys?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async createAPIKey(data: {
    provider: string;
    key_name: string;
    api_key: string;
    is_default?: boolean;
    expires_at?: string;
  }) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async updateAPIKey(keyId: string, updates: {
    key_name?: string;
    is_active?: boolean;
    is_default?: boolean;
  }) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-keys/${keyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async deleteAPIKey(keyId: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-keys/${keyId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async testAPIKey(keyId: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-keys/${keyId}/test`, {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  }
}

export const apiService = new ApiService(); 